import { PayoutListFilter, PayoutStatus } from '@frontend/api-payouts';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import {
  DateRangeField,
  DropdownField,
  FormRow,
  MoneyField,
  RadioField,
  SearchField,
  useFormField,
} from '@frontend/design-system';
import { createISODateInUTC, isoDateToDisplayInUTC } from '../../../utils/date-helpers';
import { TableFilter } from '../../TableFilter';

interface Props {
  initialFilter: PayoutListFilter;
  onApply: (filters: PayoutListFilter) => void;
  payoutDescriptors: string[];
}

const DATA_TRACKING_PREFIX = `pay-portal-payouts-filter`;

export const PayoutsFilterMenu = ({ initialFilter, onApply, payoutDescriptors }: Props) => {
  const { t } = useTranslation('payments');
  // DATE RANGE

  const payoutStatusMap = {
    PAID: t('Paid'),
    PENDING: t('Pending'),
    CANCELED: t('Canceled'),
    FAILED: t('Failed'),
    IN_TRANSIT: t('In Transit'),
  };

  const initialDates = initialFilter?.arrival;
  const dateRangeProps = useFormField({
    type: 'dateRange',
    value:
      initialDates?.gte && initialDates?.lte
        ? [isoDateToDisplayInUTC(initialDates.gte), isoDateToDisplayInUTC(initialDates.lte)]
        : [],
    maxDate: getTodaysDate('YYYY-M-D') as string,
  });

  // AMOUNT

  const initialAmount = initialFilter.amount || {};
  const amountTypeProps = useFormField({
    type: 'dropdown',
    value: Object.keys(initialAmount)[0] || 'eq',
  });

  const amountProps = useFormField({
    type: 'money',
    value: Object.values(initialAmount).length > 0 ? String(Object.values(initialAmount)[0] / 100) : '', // convert from cents
  });

  // STATUS

  const statusProps = useFormField({
    type: 'dropdown',
    value: initialFilter.status,
  });

  const payoutDescriptorProps = useFormField({
    type: 'radio',
    value: initialFilter?.descriptor || '',
  });

  const payoutDescriptorSearchProps = useFormField({
    type: 'text',
    // Only fill search field if it is not one of the radio options
    value: !payoutDescriptors.some((descriptor) => initialFilter.descriptor === descriptor)
      ? initialFilter.descriptor
      : '',
  });

  const handleApply = () => {
    const newFilters: PayoutListFilter = {};

    const [start, end] = dateRangeProps.value;
    if (start && end) {
      newFilters.arrival = {
        gte: createISODateInUTC(start),
        lte: createISODateInUTC(end),
      };
    }

    if (!!amountProps.value) {
      newFilters.amount = {
        [amountTypeProps.value]: Math.floor(+amountProps.value * 100),
      }; //convert to cents
    }

    if (!!statusProps.value) {
      newFilters.status = statusProps.value as PayoutStatus;
    }

    if (!!payoutDescriptorProps.value) {
      newFilters.descriptor = payoutDescriptorProps.value;
    }

    if (!!payoutDescriptorSearchProps.value) {
      newFilters.descriptor = payoutDescriptorSearchProps.value;
    }
    onApply(newFilters);
  };

  return (
    <>
      {Object.entries(payoutDescriptors).length > 1 && (
        <TableFilter.Option
          label={t('Payout Descriptor')}
          // calculates animation height based on the number of descriptors with default height of 3rem + 4rem per option
          animationHeight={3 + 4 * Object.keys(payoutDescriptors).length}
          initialState={!!initialFilter.descriptor}
          onHide={() => {
            payoutDescriptorProps.onChange({ value: '', name: 'filter-descriptor' });
            payoutDescriptorSearchProps.onChange({ value: '', name: 'filter-descriptor-search' });
          }}
          data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-descriptor`}
        >
          <RadioField
            {...payoutDescriptorProps}
            name='filter-descriptor'
            onChange={(e) => {
              // Clear search field when selecting a radio option
              payoutDescriptorProps.onChange(e);
              payoutDescriptorSearchProps.onChange({ value: '', name: 'filter-descriptor-search' });
            }}
          >
            {Object.entries(payoutDescriptors).map(([key, value]) => (
              <RadioField.Radio value={value} key={key}>
                {value || key}
              </RadioField.Radio>
            ))}
          </RadioField>
          <SearchField
            {...payoutDescriptorSearchProps}
            placeholder={t('Search previous descriptors')}
            name='filter-descriptor-search'
            onChange={(e) => {
              // Clear radio selection when typing in search field
              payoutDescriptorSearchProps.onChange(e);
              payoutDescriptorProps.onChange({ value: '', name: 'filter-descriptor' });
            }}
          />
        </TableFilter.Option>
      )}
      <TableFilter.Option
        label={t('Est. Arrival')}
        animationHeight={4}
        initialState={!!initialFilter.arrival}
        onHide={() => dateRangeProps.onChange({ value: [], name: 'filter date range' })}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-arrival`}
      >
        <DateRangeField
          name='filter date range'
          label={t('Filter Date')}
          {...dateRangeProps}
          css={{ marginBottom: 0 }}
        />
      </TableFilter.Option>
      <TableFilter.Option
        label={t('Amount')}
        animationHeight={4}
        initialState={!!initialFilter.amount}
        onHide={() => {
          amountProps.onChange({ name: 'filter amount', value: '' });
          amountTypeProps.onChange({ name: 'filter amount type', value: '' });
        }}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-amount`}
      >
        <FormRow>
          <DropdownField
            label={t('Type')}
            name='filter amount type'
            {...amountTypeProps}
            data-trackingid={`${DATA_TRACKING_PREFIX}-drp-amounttype`}
          >
            <DropdownField.Option value='eq'>{t('Equal To')}</DropdownField.Option>
            <DropdownField.Option value='lt'>{t('Less Than')}</DropdownField.Option>
            <DropdownField.Option value='gt'>{t('Greater Than')}</DropdownField.Option>
          </DropdownField>
          <MoneyField
            label={t('Amount')}
            name='filter amount'
            {...amountProps}
            data-trackingid={`${DATA_TRACKING_PREFIX}-txt-amount`}
          />
        </FormRow>
      </TableFilter.Option>
      <TableFilter.Option
        label={t('Status')}
        animationHeight={4}
        initialState={!!initialFilter.status}
        onHide={() => statusProps.onChange({ name: 'filter status', value: '' })}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-status`}
      >
        <DropdownField
          label={t('Select Status')}
          name='filter status'
          {...statusProps}
          data-trackingid={`${DATA_TRACKING_PREFIX}-drp-status`}
        >
          {Object.values(PayoutStatus).map((status) => {
            return (
              <DropdownField.Option
                value={status}
                key={status}
                data-trackingid={`${DATA_TRACKING_PREFIX}-drp-item-${status}`}
              >
                {payoutStatusMap[status]}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      </TableFilter.Option>
      <TableFilter.Actions trackingId={DATA_TRACKING_PREFIX} onApply={handleApply} />
    </>
  );
};
